/**
 * Stand-in ship models (M10 P1b2, hull tiers spec §6.5): what a ship shows and collides as until its streamed GLB
 * lands. A band-sized grey box from the manifest entry's `bounds`, so an AI ship never pops in as nothing.
 *
 * - `instantiate` builds an `LOD` with one level: a box on the `MAT_hull` slot (so `applyShipLook` dresses it in the
 *   faction look) plus a small `MAT_emissive` marker at every `LIGHT_` socket (so the faction light pattern reads).
 *   Geometry is shared per entry; the placeholder materials are shared by every stand-in and replaced by the look.
 * - `sockets` are empties at the manifest's socket poses with the socket `params` as `userData` (as the GLB's extras
 *   arrive), so `def.mounts` hardpoints attach, engines get their plumes and `EYE_PILOT` resolves.
 * - `collider` is `convexHulls` with one 8-corner hull at the bounds' min/max corners: an OBB, never a bounding ball
 *   (a 150 m hull gets a box, not a 75 m sphere). `createShipBody` builds it as is; no physics change.
 *
 * One stand-in per entry (cached), so views pool and compare them like real models. Built on rare paths only (a view
 * acquire or rebuild, a collider bind); never per frame.
 */
import { BoxGeometry, Float32BufferAttribute, Group, Mesh, MeshStandardMaterial, Object3D, type BufferGeometry } from 'three/webgpu'
import type { ModelEntry } from '../data/assetSchema'
import { LIGHT_FX } from '../render/materials/lights'
import { ViewLOD } from '../render/viewLod'
import type { ColliderData, LoadedModel, ModelInstance } from './loader'

interface StandInParts {
  readonly model: LoadedModel
  readonly hull: BufferGeometry
  /** The light marker: a cube the size of the light sprite, centred on its socket. */
  readonly marker: BufferGeometry
}

const cache = new WeakMap<ModelEntry, StandInParts>()
/** Every stand-in model and collider handed out (the binder tells a stand-in body from a real one). */
const standIns = new WeakSet<object>()

let materials: { hull: MeshStandardMaterial; emissive: MeshStandardMaterial } | null = null

/** The slot placeholders (grey; `applyShipLook` swaps them for the library's slot materials). */
function slotMaterials(): { hull: MeshStandardMaterial; emissive: MeshStandardMaterial } {
  materials ??= {
    hull: new MeshStandardMaterial({ name: 'MAT_hull', color: 0x808080 }),
    emissive: new MeshStandardMaterial({ name: 'MAT_emissive', color: 0xffffff }),
  }
  return materials
}

/** A box with the ship greybox attribute set: position, normal, uv and an all-zero ECL_Mask `color` (primary paint). */
function maskedBox(sx: number, sy: number, sz: number, cx: number, cy: number, cz: number): BufferGeometry {
  const g = new BoxGeometry(sx, sy, sz)
  g.translate(cx, cy, cz)
  g.setAttribute('color', new Float32BufferAttribute(new Float32Array(g.getAttribute('position').count * 4), 4))
  return g
}

/** The 8 corners of the bounds box, as one convex hull (asset-space xyz triplets). */
export function boundsCorners(entry: ModelEntry): Float32Array {
  const [x0, y0, z0] = entry.bounds.min
  const [x1, y1, z1] = entry.bounds.max
  const out = new Float32Array(24)
  let k = 0
  for (const x of [x0, x1]) {
    for (const y of [y0, y1]) {
      for (const z of [z0, z1]) {
        out[k++] = x
        out[k++] = y
        out[k++] = z
      }
    }
  }
  return out
}

function build(entry: ModelEntry): StandInParts {
  const [x0, y0, z0] = entry.bounds.min
  const [x1, y1, z1] = entry.bounds.max
  const hull = maskedBox(x1 - x0, y1 - y0, z1 - z0, (x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2)
  const marker = maskedBox(LIGHT_FX.size, LIGHT_FX.size, LIGHT_FX.size, 0, 0, 0)
  const collider: ColliderData = { kind: 'convexHulls', hulls: [boundsCorners(entry)] }
  const mats = slotMaterials()
  const model: LoadedModel = {
    entry,
    collider,
    instantiate(lodScale: number): ModelInstance {
      const root = new Group()
      root.name = entry.id
      const level = new Group()
      level.name = 'LOD0'
      const box = new Mesh(hull, mats.hull)
      box.name = 'standIn:hull'
      level.add(box)
      const sockets = new Map<string, Object3D>()
      for (const s of entry.sockets) {
        const node = new Object3D()
        node.name = s.name
        node.position.set(s.position[0], s.position[1], s.position[2])
        node.quaternion.set(s.quaternion[0], s.quaternion[1], s.quaternion[2], s.quaternion[3])
        node.userData = { ...s.params }
        root.add(node)
        sockets.set(s.name, node)
        if (s.kind === 'LIGHT') {
          // In the LOD level (asset space), so the emissive slot finds its LIGHT_ socket's colour.
          const m = new Mesh(marker, mats.emissive)
          m.name = `standIn:${s.name}`
          m.position.copy(node.position)
          level.add(m)
        }
      }
      const lod = new ViewLOD()
      lod.name = `${entry.id}:lod`
      lod.addLevel(level, (entry.lod.distances[0] ?? 0) * lodScale, entry.lod.hysteresis)
      root.add(lod)
      return { root, lod, sockets }
    },
  }
  standIns.add(model)
  standIns.add(collider)
  return { model, hull, marker }
}

/** The stand-in for `entry` (one per entry, cached). */
export function standInModel(entry: ModelEntry): LoadedModel {
  let parts = cache.get(entry)
  if (parts === undefined) {
    parts = build(entry)
    cache.set(entry, parts)
  }
  return parts.model
}

/** True for a stand-in model or a stand-in collider. */
export function isStandIn(x: LoadedModel | ColliderData | null | undefined): boolean {
  return x !== null && x !== undefined && standIns.has(x)
}
